import { Box, Heading, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { CreditAgreement, Receivable } from '@finley-technologies/finley-types';

import {
  AssignmentSummaryAccordion,
  AssignmentSummaryAccordionRow,
  StatRow,
} from './SharedComponents';
import { borrowingBaseSummaryFormatterMap } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';

type CreditFacilityBalanceSheetStats = {
  eligibleExposureOnBalanceSheet: number;
  eligibleExposureOnBalanceSheetByLoanProductId: {
    [loanProductId: string]: number;
  };
};

type BalanceSheetImpactCalcs = {
  balanceSheetExposure: number;
  balanceSheetExposureByLoanProductId: {
    [loanProductId: string]: number;
  };
  balanceSheetReceivableCount: number;
  balanceSheetReceivableCountByLoanProductId: {
    [loanProductId: string]: number;
  };
  creditFacilityBalanceSheetStats: {
    [creditFacilityId: string]: CreditFacilityBalanceSheetStats;
  };
};

const calculateBalanceSheetImpactCalcs = (
  assignmentSummaryPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview,
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[],
): BalanceSheetImpactCalcs | null => {
  const borrowingBaseReportEntries = Object.entries(
    assignmentSummaryPreview.borrowingBaseReportMap,
  );
  if (borrowingBaseReportEntries.length === 0) {
    return null;
  }

  // balance sheet calcs will be same across all borrowing base reports, so just using the first report
  const firstBorrowingBaseReport = borrowingBaseReportEntries[0][1];

  const balanceSheetImpactCalcs: BalanceSheetImpactCalcs = {
    balanceSheetExposure: firstBorrowingBaseReport.balanceSheetExposure,
    balanceSheetExposureByLoanProductId:
      firstBorrowingBaseReport.balanceSheetExposureByLoanProductId,
    balanceSheetReceivableCount:
      firstBorrowingBaseReport.balanceSheetReceivableCount,
    balanceSheetReceivableCountByLoanProductId:
      firstBorrowingBaseReport.balanceSheetReceivableCountByLoanProductId,
    creditFacilityBalanceSheetStats: {},
  };

  parsedCreditAgreements.forEach((creditAgreement) => {
    const { creditFacilityId } = creditAgreement;
    const borrowingBaseReport =
      assignmentSummaryPreview.borrowingBaseReportMap[creditFacilityId];
    balanceSheetImpactCalcs.creditFacilityBalanceSheetStats[
      creditFacilityId
    ] = {
      eligibleExposureOnBalanceSheet:
        borrowingBaseReport.eligibleExposureOnBalanceSheet,
      eligibleExposureOnBalanceSheetByLoanProductId:
        borrowingBaseReport.eligibleExposureOnBalanceSheetByLoanProductId,
    };
  });

  return balanceSheetImpactCalcs;
};

const getUniqueLoanProducts = (
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[],
) => {
  const loanProductNameMap: { [loanProductId: string]: string } = {};
  parsedCreditAgreements.forEach((creditAgreement) => {
    const {
      borrowingBaseMechanics: {
        calcAgentMechanics: { loanProductMechanics },
      },
    } = creditAgreement;

    loanProductMechanics.forEach((loanProduct) => {
      loanProductNameMap[loanProduct.entity.id] = loanProduct.entity.name;
    });
  });

  return Object.entries(loanProductNameMap).sort((lp1, lp2) => {
    if (lp1[1] < lp2[1]) {
      return -1;
    }
    if (lp1[1] > lp2[1]) {
      return 1;
    }
    return 0;
  });
};

export const BalanceSheetImpact = ({
  initialPreview,
  proposedPreview,
  parsedCreditAgreements,
  creditFacilitySlugMap,
}: {
  initialPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  proposedPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  creditFacilitySlugMap: Map<string, string>;
}) => {
  const initialBalanceSheetImpactCalcs = calculateBalanceSheetImpactCalcs(
    initialPreview,
    parsedCreditAgreements,
  );
  const proposedBalanceSheetImpactCalcs = calculateBalanceSheetImpactCalcs(
    proposedPreview,
    parsedCreditAgreements,
  );
  if (!initialBalanceSheetImpactCalcs || !proposedBalanceSheetImpactCalcs) {
    return null;
  }

  const loanProductArr = getUniqueLoanProducts(parsedCreditAgreements);

  return (
    <Box p="32px" m="32px 0" bg={colors.millet[50]}>
      <Heading size="md" mb="16px">
        Balance Sheet Impact
      </Heading>
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Remaining Exposure"
            initial={
              <Text fontWeight="medium" textAlign="justify">
                {borrowingBaseSummaryFormatterMap.currency(
                  initialBalanceSheetImpactCalcs.balanceSheetExposure,
                )}
              </Text>
            }
            proposed={
              <Text fontWeight="medium">
                {borrowingBaseSummaryFormatterMap.currency(
                  proposedBalanceSheetImpactCalcs.balanceSheetExposure,
                )}
              </Text>
            }
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(loanProductArr, (loanProduct, index) => {
              const loanProductId = loanProduct[0];
              const loanProductName = loanProduct[1];
              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`balance-sheet-exposure-${loanProductId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={loanProductName}
                        initialValue={
                          initialBalanceSheetImpactCalcs
                            .balanceSheetExposureByLoanProductId[loanProductId]
                        }
                        proposedValue={
                          proposedBalanceSheetImpactCalcs
                            .balanceSheetExposureByLoanProductId[loanProductId]
                        }
                        valueType="currency"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        fullRowBackground
                        background="medium"
                        level={2}
                      />
                    )}
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Remaining Eligible Exposure"
            initial={<></>}
            proposed={<></>}
            noBackground
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(parsedCreditAgreements, (creditAgreement, index) => {
              const {
                borrowingBaseMechanics: {
                  calcAgentMechanics: { loanProductMechanics },
                },
                creditFacilityId,
              } = creditAgreement;
              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`balance-sheet-eligible-exposure-${creditFacilityId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={creditFacilitySlugMap.get(creditFacilityId)}
                        initialValue={
                          initialBalanceSheetImpactCalcs
                            .creditFacilityBalanceSheetStats[creditFacilityId]
                            .eligibleExposureOnBalanceSheet
                        }
                        proposedValue={
                          proposedBalanceSheetImpactCalcs
                            .creditFacilityBalanceSheetStats[creditFacilityId]
                            .eligibleExposureOnBalanceSheet
                        }
                        valueType="currency"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        fullRowBackground
                        background="medium"
                        level={2}
                      />
                    )}
                    accordionPanel={
                      <Box mt="16px">
                        {_.map(loanProductMechanics, (loanProduct) => {
                          return (
                            <Box
                              mt="8px"
                              key={`remaining-receivable-count-${loanProduct.entity.id}`}
                            >
                              <AssignmentSummaryAccordionRow
                                label={loanProduct.entity.name}
                                initialValue={
                                  initialBalanceSheetImpactCalcs
                                    .creditFacilityBalanceSheetStats[
                                    creditFacilityId
                                  ]
                                    .eligibleExposureOnBalanceSheetByLoanProductId[
                                    loanProduct.entity.id
                                  ]
                                }
                                proposedValue={
                                  proposedBalanceSheetImpactCalcs
                                    .creditFacilityBalanceSheetStats[
                                    creditFacilityId
                                  ]
                                    .eligibleExposureOnBalanceSheetByLoanProductId[
                                    loanProduct.entity.id
                                  ]
                                }
                                valueType="currency"
                                background="light"
                                level={3}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    }
                  />
                </Box>
              );
            })}
          </Box>
        }
        defaultIsExpanded
      />
      <AssignmentSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <StatRow
            label="Remaining Receivable Count"
            initial={
              <Text fontWeight="medium" textAlign="justify">
                {borrowingBaseSummaryFormatterMap.integer(
                  initialBalanceSheetImpactCalcs.balanceSheetReceivableCount,
                )}
              </Text>
            }
            proposed={
              <Text fontWeight="medium">
                {borrowingBaseSummaryFormatterMap.integer(
                  proposedBalanceSheetImpactCalcs.balanceSheetReceivableCount,
                )}
              </Text>
            }
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
          />
        )}
        accordionPanel={
          <Box mt="24px">
            {_.map(loanProductArr, (loanProduct, index) => {
              const loanProductId = loanProduct[0];
              const loanProductName = loanProduct[1];
              return (
                <Box
                  mt={index > 0 ? '16px' : '0px'}
                  key={`remaining-receivable-count-${loanProductId}`}
                >
                  <AssignmentSummaryAccordion
                    accordionButton={({ hasAccordionPanel, isExpanded }) => (
                      <AssignmentSummaryAccordionRow
                        label={loanProductName}
                        initialValue={
                          initialBalanceSheetImpactCalcs
                            .balanceSheetReceivableCountByLoanProductId[
                            loanProductId
                          ]
                        }
                        proposedValue={
                          proposedBalanceSheetImpactCalcs
                            .balanceSheetReceivableCountByLoanProductId[
                            loanProductId
                          ]
                        }
                        valueType="integer"
                        hasAccordionPanel={hasAccordionPanel}
                        isExpanded={isExpanded}
                        fullRowBackground
                        background="medium"
                        level={2}
                      />
                    )}
                  />
                </Box>
              );
            })}
          </Box>
        }
      />
    </Box>
  );
};
